/**
 * Field and row locks for shared templates. Keep in step with
 * public.apply_template_lock_policy / merge_locked_rows in
 * services/supabase/migrations/20261001120000_template_field_locks.sql.
 * contact_id is never lockable.
 */

/** Settings keys a master can lock, keyed by the settings field name. */
export const TEMPLATE_LOCKABLE_FIELDS = [
    "required_events",
    "injection_id",
    "injection_method_id",
    "injection_details",
    "referring_clinician_id",
    "injecting_clinician_id",
    "surgery_id",
    "operating_clinician_id",
    "side",
    "orthopaedic_area",
    "surgery_option_id",
    "education_id",
    "education_name",
    "education_description",
    "educated_by",
    "shockwave_id",
    "shockwave_details",
    "shockwaving_clinician_id",
    "product_id",
    "product_option_id",
    "provided_by",
    "usage_instructions",
] as const;

export type TemplateLockField = (typeof TEMPLATE_LOCKABLE_FIELDS)[number];

export interface TemplateLockEntry {
    locked: boolean;
    lockedBy?: string;
    lockedAt?: string;
    lockReason?: string;
}

export type TemplateLockPolicy = Partial<Record<TemplateLockField, TemplateLockEntry>>;

/** Tracker and consent rows lock individually via a flag on the row. */
export const TEMPLATE_LOCKABLE_ROW_KEYS = ["trackers", "consents"] as const;

export interface TemplateLockRow {
    locked?: boolean;
    row_id?: string;
    [key: string]: unknown;
}

export type TemplateLockSettings = Record<string, unknown>;

export function isFieldLocked(policy: TemplateLockPolicy | null | undefined, field: TemplateLockField): boolean {
    return policy?.[field]?.locked === true;
}

export function isRowLocked(row: { locked?: boolean | null } | null | undefined): boolean {
    return row?.locked === true;
}

export function parseLockPolicy(value: unknown): TemplateLockPolicy {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const policy: TemplateLockPolicy = {};
    for (const field of TEMPLATE_LOCKABLE_FIELDS) {
        const entry = (value as Record<string, unknown>)[field];
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
        const record = entry as Record<string, unknown>;
        policy[field] = {
            locked: record.locked === true,
            ...(typeof record.lockedBy === "string" ? { lockedBy: record.lockedBy } : {}),
            ...(typeof record.lockedAt === "string" ? { lockedAt: record.lockedAt } : {}),
            ...(typeof record.lockReason === "string" ? { lockReason: record.lockReason } : {}),
        };
    }
    return policy;
}

export function setFieldLock(
    policy: TemplateLockPolicy,
    field: TemplateLockField,
    locked: boolean,
    userId: string,
    lockedAt = new Date().toISOString()
): TemplateLockPolicy {
    const previous = policy[field];
    return {
        ...policy,
        [field]: {
            locked,
            lockedBy: userId,
            lockedAt,
            ...(previous?.lockReason ? { lockReason: previous.lockReason } : {}),
        },
    };
}

/** Parent id stored on a share. A received copy keeps pointing at the master. */
export function sharedTemplateParentId(template: {
    template_id: string;
    duplicated_from_template_id?: string | null;
}): string {
    return template.duplicated_from_template_id || template.template_id;
}

/** Parent id stored on Duplicate. A personal copy of a master is a new master. */
export function duplicateTemplateParentId(template: { duplicated_from_template_id?: string | null }): string | null {
    return template.duplicated_from_template_id ?? null;
}

function asRows(value: unknown): TemplateLockRow[] {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is TemplateLockRow => !!item && typeof item === "object");
}

function stripLock(row: TemplateLockRow): TemplateLockRow {
    const { locked: _locked, ...rest } = row;
    return rest;
}

/**
 * Master locked rows first (master order), replacing the child's copy by row_id.
 * Every other child row stays; a child row whose master row is no longer locked loses its flag.
 */
export function mergeLockedRows(baseRows: unknown, sourceRows: unknown): TemplateLockRow[] {
    const locked = asRows(sourceRows).filter((row) => isRowLocked(row) && typeof row.row_id === "string");
    const lockedIds = new Set(locked.map((row) => row.row_id));
    const rest = asRows(baseRows)
        .filter((row) => !(typeof row.row_id === "string" && lockedIds.has(row.row_id)))
        .map(stripLock);
    return [...locked, ...rest];
}

/** Detail rows (injection/shockwave) lock per input via row.locked_fields. Rows match by their own id. */
export interface TemplateLockDetailRow {
    locked_fields?: string[];
    [key: string]: unknown;
}

export const TEMPLATE_LOCKABLE_DETAIL_KEYS = {
    injection_details: "injection_detail_id",
    shockwave_details: "shockwave_detail_id",
} as const;

function lockedFieldsOf(row: TemplateLockDetailRow): string[] {
    return Array.isArray(row.locked_fields) ? row.locked_fields.filter((f) => !f.endsWith("_id")) : [];
}

export function mergeLockedDetailFields(
    baseRows: unknown,
    sourceRows: unknown,
    idKey: string
): TemplateLockDetailRow[] {
    const base = asRows(baseRows) as TemplateLockDetailRow[];
    const source = (asRows(sourceRows) as TemplateLockDetailRow[]).filter(
        (row) => typeof row[idKey] === "string" && Array.isArray(row.locked_fields) && row.locked_fields.length > 0
    );
    const sourceIds = new Set(source.map((row) => row[idKey]));
    const locked = source.map((masterRow) => {
        const childRow = base.find((row) => row[idKey] === masterRow[idKey]);
        if (!childRow) return masterRow;
        let next: TemplateLockSettings = { ...childRow };
        for (const field of lockedFieldsOf(masterRow)) next = copyKey(next, masterRow, field);
        return { ...next, locked_fields: masterRow.locked_fields } as TemplateLockDetailRow;
    });
    const rest = base
        .filter((row) => !sourceIds.has(row[idKey]))
        .map(({ locked_fields: _locked, ...row }) => row as TemplateLockDetailRow);
    return [...locked, ...rest];
}

function copyKey(base: TemplateLockSettings, source: TemplateLockSettings, key: string): TemplateLockSettings {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
        return { ...base, [key]: source[key] };
    }
    if (!Object.prototype.hasOwnProperty.call(base, key)) return base;
    const next = { ...base };
    delete next[key];
    return next;
}

export function applyTemplateLockPolicy(
    base: TemplateLockSettings | null | undefined,
    source: TemplateLockSettings | null | undefined,
    policy: TemplateLockPolicy | null | undefined,
    withRows = true
): TemplateLockSettings {
    let result: TemplateLockSettings = { ...base };
    const lockedSource = source ?? {};

    for (const field of TEMPLATE_LOCKABLE_FIELDS) {
        if (isFieldLocked(policy, field)) result = copyKey(result, lockedSource, field);
    }

    if (withRows) {
        for (const key of TEMPLATE_LOCKABLE_ROW_KEYS) {
            if (!(key in result) && !(key in lockedSource)) continue;
            const merged = mergeLockedRows(result[key], lockedSource[key]);
            if (key in result || merged.length > 0) result = { ...result, [key]: merged };
        }
        for (const [key, idKey] of Object.entries(TEMPLATE_LOCKABLE_DETAIL_KEYS)) {
            if (!(key in result) && !(key in lockedSource)) continue;
            result = { ...result, [key]: mergeLockedDetailFields(result[key], lockedSource[key], idKey) };
        }
    }

    return result;
}
